import { Localized } from '../../internal/Localized';
import { useLocale } from '../../providers/LocaleProvider';
import Styles from './Landing.module.css';
import { Label } from '@app/engine';
import { useEffect, useId, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/** A single hero statistic shown inside a hexagon bubble. */
export interface IHexStat {
  value: string;
  label: string;
  title: string;
}

/** File-local constants for the hexagon stat bubbles. */
const localConstants = {
  // Pointy-top hexagon in a 100x100 viewBox (vertex at top/bottom, flat-ish sides).
  viewBox: '0 0 100 100',
  hexPoints: '50,3 92,26 92,74 50,97 8,74 8,26',
  center: 50,
  // The six vertices the decorative spokes run out to from the centre (favicon look).
  vertices: [
    [50, 3],
    [92, 26],
    [92, 74],
    [50, 97],
    [8, 74],
    [8, 26]
  ],
  orbRadius: 17,
  // Spokes run from each vertex only halfway in toward the centre (fraction of the vertex->centre distance), so they read as short rays rather than a solid cross that looks like a 3D cube.
  spokeReach: 0.5,
  // Portrait rotator timing. The content fades in with a bounce, holds, then fades out before the next stat.
  rotatorInMs: 380,
  rotatorOutMs: 200,
  rotatorHoldMs: 1000,
  // Extra first-hold offset applied to the second rotator bubble so the pair never advances in unison.
  rotatorStaggerMs: 700,
  // The two portrait bubbles split the stats in half (first half left, second half right).
  parityDivisor: 2,
  // Per-bubble wiggle stagger so a cluster never moves in unison.
  wiggleStaggerMs: 420,
  // Circular clock-arc layout (landscape): the bubbles ride a C-shape opening left (clock numbers ~1–5), centred vertically with the mid bubbles bulging right. Positions are computed in rem so the
  // spacing is fixed regardless of the cluster's stretched height — adjacent bubbles never overlap whatever the column height.
  clockStartDeg: 35,
  clockEndDeg: 145,
  arcRadiusRem: 15.5,
  arcBulgeRem: 15.5,
  degreesPerHalfCircle: 180,
  midpointDivisor: 2,
  halfTurn: 1,
  // Horizontal mirror factor: 1 keeps the LTR clock-arc, -1 flips it for RTL so the arc opens toward the content on either side.
  mirrorUnit: 1,
  // The label is drawn as fit-to-width SVG text (textLength + lengthAdjust) so a single long word in any language is condensed/expanded to the hex band rather than overflowing it. The viewBox is
  // unitless user space; textLength leaves a small inset on each side and the font-size is an attribute (user units) so it scales with the rendered bubble.
  labelViewBox: '0 0 100 18',
  labelTextX: 50,
  labelTextY: 14,
  labelTextLength: 94,
  labelFontSize: 15
} as const;

/** Props for the HexShell visual. */
interface IHexShellProps {
  children: ReactNode;
  className?: string;
  delayMs?: number;
}

/**
 * The hexagon bubble visual — a glowing cyan hex frame with a dark radial fill, six spokes and a central orb, modelled on the app favicon. Center content is supplied as children.
 *
 * @param props - Component props.
 * @returns The hexagon shell element.
 */
function HexShell(props: IHexShellProps) {
  const rawId = useId();
  // SVG gradient ids must be unique per instance; useId yields characters illegal in a url(#…) reference, so strip them.
  const id = rawId.replace(/:/g, '');
  const fillId = `${id}-fill`;
  const orbId = `${id}-orb`;
  const style = useMemo<CSSProperties>(() => ({ animationDelay: `${props.delayMs ?? 0}ms` }), [props.delayMs]);

  return (
    <div className={props.className ? `${Styles.hexBubble} ${props.className}` : Styles.hexBubble} style={style}>
      <svg className={Styles.hexSvg} viewBox={localConstants.viewBox} aria-hidden="true">
        <defs>
          <radialGradient id={fillId} cx="50%" cy="42%" r="62%">
            <stop offset="0%" className={Styles.hexStopInner} />
            <stop offset="100%" className={Styles.hexStopOuter} />
          </radialGradient>
          <radialGradient id={orbId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" className={Styles.hexOrbStopInner} />
            <stop offset="100%" className={Styles.hexOrbStopOuter} />
          </radialGradient>
        </defs>
        <polygon className={Styles.hexShape} points={localConstants.hexPoints} fill={`url(#${fillId})`} />
        {localConstants.vertices.map((vertex, position) => {
          // Inner end of the spoke: start partway out from the centre toward the vertex so the lines never meet in the middle.
          const innerX = localConstants.center + (vertex[0] - localConstants.center) * localConstants.spokeReach;
          const innerY = localConstants.center + (vertex[1] - localConstants.center) * localConstants.spokeReach;
          return <line key={position} className={Styles.hexSpoke} x1={innerX} y1={innerY} x2={vertex[0]} y2={vertex[1]} />;
        })}
        <circle cx={localConstants.center} cy={localConstants.center} r={localConstants.orbRadius} fill={`url(#${orbId})`} />
      </svg>
      <div className={Styles.hexContent}>{props.children}</div>
    </div>
  );
}

/**
 * Center content for a single stat — value over label. The label is rendered as fit-to-width SVG text so a single long word scales to the hex band instead of overflowing.
 *
 * @param props - The stat value and label.
 * @returns The stat content fragment.
 */
function HexStatContent(props: IHexStat) {
  return (
    <>
      <Label text={props.value} classNameContainer={Styles.hexValue} title={props.title} />
      <svg className={Styles.hexLabelSvg} viewBox={localConstants.labelViewBox} preserveAspectRatio="xMidYMid meet" role="img" aria-label={props.label}>
        <title>{props.title}</title>
        <text
          x={localConstants.labelTextX}
          y={localConstants.labelTextY}
          textAnchor="middle"
          textLength={localConstants.labelTextLength}
          lengthAdjust="spacingAndGlyphs"
          fontSize={localConstants.labelFontSize}
          className={Styles.hexLabelText}
        >
          {props.label}
        </text>
      </svg>
    </>
  );
}

/** Props for the stat layouts. */
interface IHexStatsProps {
  stats: IHexStat[];
}

/**
 * Landscape stat layout — the bubbles curve down the right of the hero content like clock numbers 1 through 5. Each slot is absolutely placed along a vertical arc that bulges rightward mid-way.
 *
 * @param props - Component props.
 * @returns The clock-arc cluster element.
 */
export function HexStatCluster(props: IHexStatsProps) {
  const { stats } = props;
  const { isRTL } = useLocale();
  // The clock-arc opens left and bulges right in LTR; in RTL the whole cluster mirrors so it opens right and bulges left, keeping it visually anchored toward the content like LTR.
  const directionSign = isRTL ? -localConstants.mirrorUnit : localConstants.mirrorUnit;
  const span = Math.max(stats.length - localConstants.halfTurn, localConstants.halfTurn);
  // Raw unit-circle coordinates for each bubble (x right, y down), then centre the whole arc so it sits balanced inside the cluster box.
  const points = stats.map((_, position) => {
    const fraction = position / span;
    const deg = localConstants.clockStartDeg + fraction * (localConstants.clockEndDeg - localConstants.clockStartDeg);
    const rad = (deg * Math.PI) / localConstants.degreesPerHalfCircle;
    return { x: Math.sin(rad), y: -Math.cos(rad) };
  });
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const xMid = (Math.max(...xs) + Math.min(...xs)) / localConstants.midpointDivisor;
  const yMid = (Math.max(...ys) + Math.min(...ys)) / localConstants.midpointDivisor;

  return (
    <div className={Styles.hexCluster} aria-hidden="true">
      {stats.map((stat, position) => {
        const offsetX = (points[position].x - xMid) * localConstants.arcBulgeRem * directionSign;
        const offsetY = (points[position].y - yMid) * localConstants.arcRadiusRem;
        const slotStyle: CSSProperties = { transform: `translate(calc(-50% + ${offsetX}rem), calc(-50% + ${offsetY}rem))` };
        return (
          <div key={position} className={Styles.hexSlot} style={slotStyle}>
            <HexShell delayMs={position * localConstants.wiggleStaggerMs}>
              <HexStatContent value={stat.value} label={stat.label} title={stat.title} />
            </HexShell>
          </div>
        );
      })}
    </div>
  );
}

/** Animation phase for the portrait rotator. */
type RotatorPhase = 'in' | 'out';

/** Props for a single rotating bubble. */
interface IHexRotatorBubbleProps {
  stats: IHexStat[];
  delayMs?: number;
  startOffsetMs?: number;
  className?: string;
}

/**
 * A single rotating hexagon bubble that cycles through its own subset of stats. Content fades in with a bounce at full alpha, holds for a second, then fades out and advances.
 *
 * @param props - Component props.
 * @returns The rotating bubble element, or null when handed no stats.
 */
function HexRotatorBubble(props: IHexRotatorBubbleProps) {
  const { stats } = props;
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<RotatorPhase>('in');
  const [started, setStarted] = useState(false);

  // Drive the cycle with timers (not animationend) so reduced-motion users, where the visual animation is suppressed, still advance through every stat.
  useEffect(() => {
    if (stats.length <= localConstants.halfTurn) return;
    if (phase === 'in') {
      // Offset the first hold of the second bubble so the two hexes never advance in unison.
      const extra = started ? 0 : (props.startOffsetMs ?? 0);
      const id = window.setTimeout(
        () => {
          setStarted(true);
          setPhase('out');
        },
        localConstants.rotatorInMs + localConstants.rotatorHoldMs + extra
      );
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      setIndex((current) => (current + 1) % stats.length);
      setPhase('in');
    }, localConstants.rotatorOutMs);
    return () => window.clearTimeout(id);
  }, [phase, index, stats.length, started, props.startOffsetMs]);

  if (stats.length === 0) return null;
  const stat = stats[index] ?? stats[0];
  const phaseClass = phase === 'in' ? Styles.hexRotIn : Styles.hexRotOut;

  return (
    <HexShell delayMs={props.delayMs} className={props.className}>
      <div key={`${phase}-${index}`} className={`${Styles.hexContentAnim} ${phaseClass}`}>
        <HexStatContent value={stat.value} label={stat.label} title={stat.title} />
      </div>
    </HexShell>
  );
}

/**
 * Portrait / limited-space stat layout — two bubbles under the CTA. The first hex rotates through the first half of the stats, the second hex through the second half, so every stat is seen
 * without a single bubble cycling the full set.
 *
 * @param props - Component props.
 * @returns The dual rotating bubble element.
 */
export function HexStatRotator(props: IHexStatsProps) {
  const { stats } = props;
  if (stats.length === 0) return null;
  const split = Math.ceil(stats.length / localConstants.parityDivisor);
  const firstHalf = stats.slice(0, split);
  const secondHalf = stats.slice(split);

  return (
    <div className={Styles.hexRotatorWrap}>
      <HexRotatorBubble stats={firstHalf} />
      {secondHalf.length > 0 && <HexRotatorBubble stats={secondHalf} delayMs={localConstants.wiggleStaggerMs} startOffsetMs={localConstants.rotatorStaggerMs} className={Styles.hexWiggleAlt} />}
    </div>
  );
}

/** @returns The four hero stats, freshly read from the active translation. */
export function ReadHeroStats(): IHexStat[] {
  return [
    { value: Localized.LandingHeroStat1Value, label: Localized.LandingHeroStat1Label, title: Localized.LandingHeroStat1Title },
    { value: Localized.LandingHeroStat2Value, label: Localized.LandingHeroStat2Label, title: Localized.LandingHeroStat2Title },
    { value: Localized.LandingHeroStat3Value, label: Localized.LandingHeroStat3Label, title: Localized.LandingHeroStat3Title },
    { value: Localized.LandingHeroStat4Value, label: Localized.LandingHeroStat4Label, title: Localized.LandingHeroStat4Title }
  ];
}
